import * as React from 'react';

import { MenuItem, TextField } from '@mui/material';
import { localDate, resolveMax } from 'services/jobSpecs';
import type { JobField, JobValues } from 'services/jobSpecs';

/**
 * Renders a job spec's fields. Knows the three field kinds and nothing about
 * any particular job.
 */

type Props = {
  fields: readonly JobField[];
  values: JobValues;
  onChange: (name: string, value: string | number) => void;
  disabled?: boolean;
};

const JobFields: React.FC<Props> = ({ fields, values, onChange, disabled }) => {
  const today = localDate();

  return (
    <>
      {fields.map((field) => {
        const value = values[field.name] ?? '';

        if (field.type === 'select') {
          return (
            <TextField
              key={field.name}
              select
              fullWidth
              size="small"
              margin="dense"
              disabled={disabled}
              label={field.label}
              helperText={field.help}
              value={value}
              onChange={(e) => onChange(field.name, e.target.value)}
            >
              {field.options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          );
        }

        if (field.type === 'number') {
          const max = resolveMax(field, values);
          return (
            <TextField
              key={field.name}
              type="number"
              fullWidth
              size="small"
              margin="dense"
              disabled={disabled}
              label={field.label}
              helperText={
                max !== undefined
                  ? `${field.help ? `${field.help} ` : ''}Maximum ${max}.`
                  : field.help
              }
              inputProps={{ min: field.min, max }}
              value={value}
              onChange={(e) => onChange(field.name, e.target.value)}
            />
          );
        }

        return (
          <TextField
            key={field.name}
            type="date"
            fullWidth
            size="small"
            margin="dense"
            disabled={disabled}
            label={field.label}
            helperText={field.help}
            InputLabelProps={{ shrink: true }}
            // Bounded rather than validated after the fact: a later date
            // selects the same reviews as today, so the picker should not
            // offer one.
            inputProps={field.noLaterThanToday ? { max: today } : undefined}
            value={value}
            onChange={(e) => onChange(field.name, e.target.value)}
          />
        );
      })}
    </>
  );
};

export default JobFields;
